import React, { useEffect, useState, useRef, Component } from 'react';
import { ArrowRightIcon, ZapIcon } from 'lucide-react';
export const HowWeInvestPage = () => {
  useEffect(() => {
    document.title = 'How We Invest | Race Capital';
  }, []);
  const [activeStep, setActiveStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const processRef = useRef(null);
  const pitchUsUrl = 'https://airtable.com/appMNWbdHmSVm3l1M/shruyLbAMcJUdRUj6';
  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  useEffect(() => {
    // Only enable scroll animation on desktop
    if (isMobile) return;
    const handleScroll = () => {
      if (!processRef.current) return;
      const section = processRef.current;
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const windowHeight = window.innerHeight;
      // Check if section is in viewport
      if (sectionTop <= 100 && sectionTop > -sectionHeight + windowHeight) {
        // Calculate which step should be active based on scroll position
        const scrollProgress = Math.abs(sectionTop - 100) / (sectionHeight - windowHeight + 100);
        const stepIndex = Math.min(Math.floor(scrollProgress * 5), 4);
        setActiveStep(stepIndex);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);
  const investmentAreas = [{
    title: 'AI Infrastructure',
    description: 'AI infrastructure (inference, retrieval, orchestration) and developer tools that power modern applications and services.',
    icon: '◬'
  }, {
    title: 'Developer Tools & Data',
    description: 'Developer tools and data platforms. Open source systems with real community pull.',
    icon: '⚙'
  }, {
    title: 'Enterprise Security',
    description: 'Enterprise security and compliance infrastructure for businesses to operate more efficiently.',
    icon: '⬡'
  }, {
    title: 'AI-Native Software',
    description: 'AI-native vertical SaaS for B2B markets. Enterprise solutions built for the AI era.',
    icon: '⬆'
  }];
  const quickFacts = [{
    label: 'AVG CHECK SIZE',
    value: '$4-8M'
  }, {
    label: 'Position',
    value: 'Lead'
  }, {
    label: 'Decision Timeline',
    value: 'Weeks, not months'
  }, {
    label: 'Follow-on Capital',
    value: 'Follow on up to Series A'
  }, {
    label: 'Focus',
    value: 'AI Infrastructure, Enterprise Software, Security, Developer Tools, AI-Native Software'
  }, {
    label: 'Geography',
    value: 'US Based, primarily SF Bay Area'
  }];
  const processSteps = [{
    number: '01',
    title: 'Initial Meeting',
    description: '30-45 minute conversation to understand your vision and assess fit. Live demo preferred.'
  }, {
    number: '02',
    title: 'Deep Dive',
    description: "We download and run your product. If serious, we'll intro real customers and observe sales calls."
  }, {
    number: '03',
    title: 'Partner Meeting',
    description: '60+ minute deep dive into team, tech, GTM, and traction with our team and advisors.'
  }, {
    number: '04',
    title: 'Term Sheet',
    description: 'Move quickly to provide terms and finalize agreement.'
  }, {
    number: '05',
    title: 'Partnership Begins',
    description: 'Ongoing support, guidance, and resources for growth. We stay committed from first check to breakout round.'
  }];
  return <div className="w-full bg-white mt-20" data-id="element-295">
      {/* Hero Section */}
      <section className="w-full pt-20 pb-20 px-6 md:px-12 bg-gradient-to-b from-white to-gray-50/50" data-id="element-296">
        <div className="max-w-7xl mx-auto" data-id="element-297">
          <div className="relative mb-6 overflow-visible" data-id="element-298">
            {/* Racing stripes decoration - animated and flowing */}
            <style jsx data-id="element-299">{`
              @keyframes fillFromLeft {
                from {
                  transform: scaleX(0);
                  transform-origin: left;
                }
                to {
                  transform: scaleX(1);
                  transform-origin: left;
                }
              }
              .racing-stripe {
                transform: scaleX(0);
                transform-origin: left;
                animation: fillFromLeft 0.8s ease-out forwards;
              }
              .racing-stripe:nth-child(1) {
                animation-delay: 0.1s;
              }
              .racing-stripe:nth-child(2) {
                animation-delay: 0.2s;
              }
              .racing-stripe:nth-child(3) {
                animation-delay: 0.3s;
              }
              .racing-stripe:nth-child(4) {
                animation-delay: 0.4s;
              }
            `}</style>
            <div className="absolute right-[12px] top-0 hidden md:flex flex-col space-y-2 pointer-events-none" data-id="element-300">
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/30 to-red-600/10" style={{
              width: '240px',
              marginLeft: '0px'
            }} data-id="element-301"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/35 to-red-600/15" style={{
              width: '200px',
              marginLeft: '40px'
            }} data-id="element-302"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/40 to-red-600/20" style={{
              width: '160px',
              marginLeft: '80px'
            }} data-id="element-303"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/45 to-red-600/25" style={{
              width: '120px',
              marginLeft: '120px'
            }} data-id="element-304"></div>
            </div>
            <h1 className="relative z-10 text-[clamp(2.5rem,5vw,4rem)] font-semibold text-[#070707] tracking-tight leading-[1.15]" data-id="element-305">
              How We Invest
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mb-16 leading-relaxed font-light" data-id="element-306">
            Race Capital partners with exceptional entrepreneurs building
            category-defining companies in enterprise, infrastructure, and
            beyond.
          </p>
          <div className="bg-white p-8 md:p-10 rounded-2xl border border-gray-200 shadow-sm mt-16" data-id="element-307">
            <p className="text-lg text-gray-700 leading-relaxed italic" data-id="element-308">
              "We don't just invest capital. We invest our time, network, and
              expertise to help founders navigate the challenges of building
              transformative companies."
            </p>
            <p className="text-red-600 font-semibold mt-6" data-id="element-309">
              — Race Capital Team
            </p>
          </div>
        </div>
      </section>
      {/* Quick Facts Section */}
      <section className="w-full py-20 px-6 md:px-12 bg-white" data-id="element-310">
        <div className="max-w-7xl mx-auto" data-id="element-311">
          <div className="mb-16" data-id="element-312">
            <h2 className="text-4xl md:text-5xl font-bold text-[#070707] mb-4 tracking-tight" data-id="element-313">
              Investment Overview
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed font-light" data-id="element-314">
              Here's what you need to know before our first conversation.
            </p>
          </div>
          <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-315">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-id="element-316">
              {quickFacts.map((fact, index) => <div key={index} className="p-10 border-b md:border-r border-gray-100 last:border-b-0 md:last:border-r-0 lg:[&:nth-child(3n)]:border-r-0 hover:bg-gray-50/50 transition-all relative group" data-id="element-317">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-600 transform scale-y-0 group-hover:scale-y-100 transition-all origin-top" data-id="element-318"></div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-4" data-id="element-319">
                    {fact.label}
                  </p>
                  <p className="text-xl text-gray-900 group-hover:text-red-600 font-semibold transition-colors" data-id="element-320">
                    {fact.value}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Investment Process Section - Mobile: Simple List, Desktop: Scroll Animation */}
      <section ref={processRef} className={`w-full py-20 px-6 md:px-12 bg-gradient-to-b from-gray-50/50 to-white ${!isMobile ? 'md:min-h-[300vh]' : ''}`} data-id="element-321">
        <div className={`max-w-7xl mx-auto ${!isMobile ? 'md:sticky md:top-24' : ''}`} data-id="element-322">
          <h2 className="text-4xl font-bold text-[#070707] mb-6 tracking-tight" data-id="element-323">
            Our Investment Process
          </h2>
          <p className="text-lg text-gray-600 mb-16 max-w-3xl leading-relaxed" data-id="element-324">
            We move quickly and efficiently. From first meeting to term sheet,
            we aim to make decisions in weeks, not months.
          </p>
          {/* Progress Bar - Hidden on mobile */}
          <div className="mb-12 px-4 hidden md:block" data-id="element-325">
            <div className="relative flex items-center justify-between max-w-4xl mx-auto" data-id="element-326">
              {/* Background line */}
              <div className="absolute left-0 right-0 top-1/2 h-px bg-gray-200 -translate-y-1/2" data-id="element-327"></div>
              {/* Progress line */}
              <div className="absolute left-0 top-1/2 h-px bg-red-600 -translate-y-1/2 transition-all duration-500" style={{
              width: `${activeStep / (processSteps.length - 1) * 100}%`
            }} data-id="element-328"></div>
              {/* Dots */}
              {processSteps.map((step, index) => <div key={index} className="relative z-10" data-id="element-329">
                  <div className={`w-2 h-2 rounded-full transition-all duration-500 ${index <= activeStep ? 'bg-red-600 scale-125' : 'bg-gray-300'}`} data-id="element-330"></div>
                </div>)}
            </div>
          </div>
          {/* Mobile: Vertical List */}
          <div className="md:hidden space-y-6" data-id="element-331">
            {processSteps.map((step, index) => <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-332">
                <div className="text-3xl font-bold text-red-600 mb-4" data-id="element-333">
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold mb-3 text-[#070707]" data-id="element-334">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-335">
                  {step.description}
                </p>
              </div>)}
          </div>
          {/* Desktop: Grid with scroll animation */}
          <div className="hidden md:block bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm" data-id="element-336">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5" data-id="element-337">
              {processSteps.map((step, index) => <div key={index} className={`p-8 border-b md:border-r border-gray-200 last:border-b-0 md:last:border-r-0 lg:[&:nth-child(5n)]:border-r-0 transition-all duration-500 relative ${activeStep === index ? 'bg-red-50' : 'bg-white hover:bg-gray-50'}`} data-id="element-338">
                  <div className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-500 ${activeStep === index ? 'bg-red-600 scale-y-100' : 'bg-gray-300 scale-y-0'} origin-top`} data-id="element-339"></div>
                  <div className={`text-4xl font-bold mb-6 transition-colors duration-500 ${activeStep === index ? 'text-red-600' : 'text-red-600/20'}`} data-id="element-340">
                    {step.number}
                  </div>
                  <h3 className={`text-lg font-semibold mb-4 transition-colors duration-500 ${activeStep === index ? 'text-red-600' : 'text-[#070707]'}`} data-id="element-341">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-342">
                    {step.description}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Investment Philosophy */}
      <section className="w-full py-20 px-6 md:px-12 bg-white relative overflow-hidden" data-id="element-343">
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-344">
          <h2 className="text-4xl font-bold text-[#070707] mb-16 tracking-tight" data-id="element-345">
            Our Investment Philosophy
          </h2>
          <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-346">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" data-id="element-347">
              {[{
              icon: '⚮',
              title: 'Founder-First',
              isIconComponent: false,
              iconSize: 'text-[75px]',
              points: ['High conviction approach', 'Pre-seed through Series A+', 'Long-term commitment', 'Generational companies']
            }, {
              icon: '∞',
              title: 'Long-Term Vision',
              isIconComponent: false,
              iconSize: 'text-[75px]',
              points: ['Category-defining potential', '$10B+ outcome path', 'Markets worth winning', 'Bold AI-era solutions']
            }, {
              icon: '⚯',
              title: 'Active Partnership',
              isIconComponent: false,
              iconSize: 'text-[75px]',
              points: ['GTM muscle building', 'Sales & customer intros', 'Hiring & pricing support', '$5M+ ARR readiness']
            }, {
              icon: 'zap',
              title: 'Speed & Conviction',
              isIconComponent: true,
              points: ['Fast yes or no decisions', 'Weeks, not months', 'Streamlined process', 'Responsive to founders']
            }].map((item, index) => <div key={index} className="p-10 border-b md:border-r border-gray-100 last:border-b-0 md:last:border-r-0 lg:[&:nth-child(4n)]:border-r-0 hover:bg-gray-50/50 transition-all relative group overflow-hidden min-h-[280px]" data-id="element-348">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-600 transform scale-y-0 group-hover:scale-y-100 transition-all origin-top" data-id="element-349"></div>
                  {/* Icon in bottom-right corner with low opacity */}
                  <div className={`absolute bottom-4 right-4 text-red-600 pointer-events-none ${index === 3 ? 'opacity-[0.25]' : 'opacity-[0.08]'}`} data-id="element-350">
                    {item.isIconComponent ? <ZapIcon size={75} strokeWidth={1} data-id="element-351" /> : <div className={`${item.iconSize} font-light leading-none`} data-id="element-352">
                        {item.icon}
                      </div>}
                  </div>
                  {/* Content positioned at top */}
                  <div className="relative z-10" data-id="element-353">
                    <h3 className="text-xl font-semibold mb-6 text-[#070707] group-hover:text-red-600 transition-colors" data-id="element-354">
                      {item.title}
                    </h3>
                    <ul className="space-y-3" data-id="element-355">
                      {item.points.map((point, pointIndex) => <li key={pointIndex} className="text-gray-600 text-sm flex items-start" data-id="element-356">
                          <span className="text-red-600 mr-3 mt-0.5" data-id="element-357">✓</span>
                          {point}
                        </li>)}
                    </ul>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Investment Areas */}
      <section className="w-full py-20 px-6 md:px-12 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden" data-id="element-358">
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-359">
          <h2 className="text-4xl font-bold text-[#070707] mb-16 tracking-tight" data-id="element-360">
            Investment Focus Areas
          </h2>
          <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-361">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" data-id="element-362">
              {[{
              title: 'AI Infrastructure',
              description: 'AI infrastructure (inference, retrieval, orchestration) and developer tools that power modern applications and services.',
              icon: '◬'
            }, {
              title: 'Developer Tools & Data',
              description: 'Developer tools and data platforms. Open source systems with real community pull.',
              icon: '⚙'
            }, {
              title: 'Enterprise Security',
              description: 'Enterprise security and compliance infrastructure for businesses to operate more efficiently.',
              icon: '⬡'
            }, {
              title: 'AI-Native Software',
              description: 'AI-native vertical SaaS for B2B markets. Enterprise solutions built for the AI era.',
              icon: '⬆'
            }].map((area, index) => <div key={index} className="p-10 border-b md:border-r border-gray-100 last:border-b-0 md:last:border-r-0 lg:[&:nth-child(4n)]:border-r-0 hover:bg-gray-50/50 transition-all relative group overflow-hidden min-h-[280px]" data-id="element-363">
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-600 transform scale-y-0 group-hover:scale-y-100 transition-all origin-top" data-id="element-364"></div>
                  {/* Icon in bottom-right corner with low opacity */}
                  <div className="absolute bottom-4 right-4 text-[90px] text-red-600 opacity-[0.08] font-light pointer-events-none leading-none" data-id="element-365">
                    {area.icon}
                  </div>
                  {/* Content positioned at top */}
                  <div className="relative z-10" data-id="element-366">
                    <h3 className="text-xl font-semibold mb-4 text-[#070707] group-hover:text-red-600 transition-colors" data-id="element-367">
                      {area.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm" data-id="element-368">
                      {area.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="w-full py-20 px-6 md:px-12 bg-gradient-to-b from-gray-50/50 to-white" data-id="element-369">
        <div className="max-w-7xl mx-auto" data-id="element-370">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-center justify-between border border-gray-200 shadow-xl" data-id="element-371">
            <div className="mb-10 md:mb-0 md:mr-12" data-id="element-372">
              <h2 className="text-4xl font-bold text-[#070707] mb-6 tracking-tight" data-id="element-373">
                Ready to Partner?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl leading-relaxed" data-id="element-374">
                If you're building something transformative and looking for a
                partner who can help accelerate your journey, we'd love to hear
                from you.
              </p>
            </div>
            <a href={pitchUsUrl} target="_blank" rel="noopener noreferrer" className="bg-red-600 hover:bg-red-700 text-white font-semibold py-4 px-10 rounded-lg transition-all hover:shadow-xl hover:shadow-red-600/20 whitespace-nowrap" data-id="element-375">
              Pitch Us
            </a>
          </div>
        </div>
      </section>
    </div>;
};